/* eslint-disable no-undef */
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../dashboard/DashboardLayout';
import SettingsWizard from '../../components/SettingsWizard';
import { checkWizardCompletion } from '../../utils/wizardUtils';
import { 
  Filter, 
  X, 
  Plus, 
  Sparkles, 
  Check, 
  ShieldAlert, 
  ShieldCheck, 
  Zap, 
  Trash2, 
  Search,
  Target,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { trackPageView, trackButtonClick } from '../../utils/analytics';
import axiosInstance from '../../utils/axiosInstance';

const FilterSettings = () => {
  const [titleSkipEnabled, setTitleSkipEnabled] = useState(false);
  const [titleFilterEnabled, setTitleFilterEnabled] = useState(false);
  const [badWordsEnabled, setBadWordsEnabled] = useState(false);
  const [titleSkipWords, setTitleSkipWords] = useState([]);
  const [titleFilterWords, setTitleFilterWords] = useState([]);
  const [badWords, setBadWords] = useState([]);
  const [newTitleSkip, setNewTitleSkip] = useState('');
  const [newTitleFilter, setNewTitleFilter] = useState('');
  const [newBadWord, setNewBadWord] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStatus, setWizardStatus] = useState({ completed: false });
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    trackPageView("/filter-settings", "Filter Settings");
    loadFilters();
    checkWizardStatus();
  }, []);

  const checkWizardStatus = async () => {
    try {
      const completion = await checkWizardCompletion();
      setWizardStatus(completion);
    } catch (e) {
      console.error('Failed to check wizard status:', e);
    }
  };

  const handleWizardComplete = () => {
    localStorage.setItem('applyease_wizard_completed', 'true');
    setShowWizard(false);
    checkWizardStatus();
    loadFilters(); 
  };

  const loadFilters = async () => {
    setLoading(true);
    try {
      // 1. Load from Backend
      const userResp = await axiosInstance.get('/user');
      if (userResp.data?.settings) {
        const s = userResp.data.settings;
        setTitleSkipEnabled(s.titleSkipEnabled || false);
        setTitleFilterEnabled(s.titleFilterEnabled || false);
        setBadWordsEnabled(s.badWordsEnabled || false);
        setTitleSkipWords(s.titleSkipWords || []);
        setTitleFilterWords(s.titleFilterWords || []);
        setBadWords(s.badWords || []);
      }

      // 2. Load from Chrome Extension (as fallback/secondary sync)
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        chrome.runtime.sendMessage({ action: 'getFilterSettings' }, (response) => {
          if (response && !chrome.runtime.lastError) {
             // We prioritize backend data if available, but can merge or overwrite here if needed
          }
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const saveFilters = async () => {
    trackButtonClick("Save Filters", "Filter Settings");
    setSyncing(true);
    setSaveSuccess(false);
    const filterData = {
      titleSkipEnabled,
      titleFilterEnabled,
      badWordsEnabled,
      titleSkipWords,
      titleFilterWords,
      badWords
    };

    try {
      // 1. Save to Backend
      const formData = new FormData();
      formData.append('settings', JSON.stringify(filterData));
      await axiosInstance.patch('/user', formData);

      // 2. Save to Chrome Extension
      if (typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage) {
        chrome.runtime.sendMessage({
          action: 'setFilterSettings',
          data: filterData
        }, () => {
          setSyncing(false);
          setSaveSuccess(true);
          setTimeout(() => setSaveSuccess(false), 2000);
        });
      } else {
        setSyncing(false);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2000);
      }
    } catch (error) {
      setSyncing(false);
      console.error('Failed to save filters:', error);
    }
  };

  const addWord = (word, type) => {
    const trimmed = word.trim();
    if (!trimmed) return;
    
    if (type === 'skip') {
      if (!titleSkipWords.includes(trimmed)) setTitleSkipWords([...titleSkipWords, trimmed]);
      setNewTitleSkip('');
    } else if (type === 'filter') {
      if (!titleFilterWords.includes(trimmed)) setTitleFilterWords([...titleFilterWords, trimmed]);
      setNewTitleFilter('');
    } else if (type === 'bad') {
      if (!badWords.includes(trimmed)) setBadWords([...badWords, trimmed]);
      setNewBadWord('');
    }
  };

  const removeWord = (word, type) => {
    if (type === 'skip') setTitleSkipWords(titleSkipWords.filter(w => w !== word));
    else if (type === 'filter') setTitleFilterWords(titleFilterWords.filter(w => w !== word));
    else if (type === 'bad') setBadWords(badWords.filter(w => w !== word));
  };

  if (showWizard) {
    return (
      <DashboardLayout activeTab="filter-settings">
        <SettingsWizard onComplete={handleWizardComplete} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout activeTab="filter-settings">
      <div className="max-w-6xl mx-auto space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-20">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
           <div className="flex flex-col text-center md:text-left">
              <h2 className="text-3xl font-black text-slate-900 tracking-tight italic flex items-center justify-center md:justify-start gap-3">
                 <ShieldCheck size={28} className="text-blue-600" /> Filter Engine
              </h2>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mt-1 ml-1">Job Filtering & Blacklist Rules</p>
           </div>

           <div className="flex items-center gap-4">
              <button
                onClick={saveFilters}
                disabled={syncing}
                className={`px-8 py-3 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 italic active:scale-95 shadow-xl ${
                  saveSuccess 
                    ? 'bg-emerald-500 text-white shadow-emerald-500/20' 
                    : 'bg-slate-900 text-white hover:bg-black shadow-slate-900/20'
                }`}
              >
                {syncing ? <RefreshCw size={16} className="animate-spin" /> : saveSuccess ? <Check size={16} /> : <Zap size={16} />}
                {syncing ? 'Syncing...' : saveSuccess ? 'Saved' : 'Save Changes'}
              </button>

              <button
                onClick={() => setShowWizard(true)}
                className="px-8 py-3 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:shadow-2xl hover:shadow-blue-200 transition-all flex items-center gap-2 italic active:scale-95 shadow-xl shadow-blue-100"
              >
                <Sparkles size={16} className="fill-white" /> Launch Setup Wizard
              </button>
           </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
           
           <div className="lg:col-span-8 space-y-8">
              
              {/* Skip Logic (High Severity) */}
              <div className={`bg-white border rounded-[3rem] p-8 shadow-sm transition-all duration-500 overflow-hidden relative ${titleSkipEnabled ? 'border-rose-200 ring-4 ring-rose-50' : 'border-slate-200 opacity-80'}`}>
                 <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none -rotate-12"><ShieldAlert size={120} className="text-rose-500"/></div>
                 
                 <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                       <div className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center transition-colors ${titleSkipEnabled ? 'bg-rose-100 text-rose-600 group-hover:scale-110' : 'bg-slate-100 text-slate-400'}`}>
                          <ShieldAlert size={24} />
                       </div>
                       <div>
                          <h4 className="text-lg font-black text-slate-900 italic">Skip Keywords</h4>
                         <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Instant Skip</p>
                       </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer scale-110">
                       <input type="checkbox" className="sr-only peer" checked={titleSkipEnabled} onChange={(e) => setTitleSkipEnabled(e.target.checked)} />
                       <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-rose-500 shadow-inner"></div>
                    </label>
                 </div>

                 {titleSkipEnabled && (
                    <div className="space-y-6 animate-in slide-in-from-top-4 duration-300">
                       <div className="flex gap-3">
                          <input
                            type="text"
                            className="flex-1 px-6 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-rose-50 focus:border-rose-400 transition-all border-b-4 shadow-inner italic"
                            placeholder="Add rejection keyword (e.g. Intern)..."
                            value={newTitleSkip}
                            onChange={(e) => setNewTitleSkip(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addWord(newTitleSkip, 'skip')}
                          />
                          <button onClick={() => addWord(newTitleSkip, 'skip')} className="px-6 bg-rose-500 text-white rounded-2xl hover:bg-rose-600 shadow-xl shadow-rose-100 transition-all active:scale-95"><Plus size={20}/></button>
                       </div>
                       <div className="flex flex-wrap gap-2">
                          {titleSkipWords.map(w => (
                             <span key={w} className="px-4 py-2 bg-rose-50 text-rose-600 border border-rose-100 rounded-xl text-xs font-black italic flex items-center gap-2 group/tag transition-all hover:bg-white">
                                {w} <X size={14} className="cursor-pointer hover:text-rose-900" onClick={() => removeWord(w, 'skip')} />
                             </span>
                          ))}
                       </div>
                    </div>
                 )}
              </div>

              {/* Inclusion Logic (Filter) */}
              <div className={`bg-white border rounded-[3rem] p-8 shadow-sm transition-all duration-500 overflow-hidden relative ${titleFilterEnabled ? 'border-blue-200 ring-4 ring-blue-50' : 'border-slate-200 opacity-80'}`}>
                 <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none -rotate-12"><Target size={120} className="text-blue-500"/></div>
                 
                 <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                       <div className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center transition-colors ${titleFilterEnabled ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>
                          <Target size={24} />
                       </div>
                       <div>
                          <h4 className="text-lg font-black text-slate-900 italic">Core Mission Targets</h4>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Must Include</p>
                       </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer scale-110">
                       <input type="checkbox" className="sr-only peer" checked={titleFilterEnabled} onChange={(e) => setTitleFilterEnabled(e.target.checked)} />
                       <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600 shadow-inner"></div>
                    </label>
                 </div>

                 {titleFilterEnabled && (
                    <div className="space-y-6 animate-in slide-in-from-top-4 duration-300">
                       <div className="flex gap-3">
                          <input
                            type="text"
                            className="flex-1 px-6 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-blue-50 focus:border-blue-400 transition-all border-b-4 shadow-inner italic"
                            placeholder="Add must-have keyword (e.g. Engineer)..."
                            value={newTitleFilter}
                            onChange={(e) => setNewTitleFilter(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addWord(newTitleFilter, 'filter')}
                          />
                          <button onClick={() => addWord(newTitleFilter, 'filter')} className="px-6 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all active:scale-95"><Plus size={20}/></button>
                       </div>
                       <div className="flex flex-wrap gap-2">
                          {titleFilterWords.map(w => (
                             <span key={w} className="px-4 py-2 bg-blue-50 text-blue-600 border border-blue-100 rounded-xl text-xs font-black italic flex items-center gap-2 group/tag transition-all hover:bg-white">
                                {w} <X size={14} className="cursor-pointer hover:text-blue-900" onClick={() => removeWord(w, 'filter')} />
                             </span>
                          ))}
                       </div>
                    </div>
                 )}
              </div>

              {/* Blacklist Logic (Bad Words) */}
              <div className={`bg-white border rounded-[3rem] p-8 shadow-sm transition-all duration-500 overflow-hidden relative ${badWordsEnabled ? 'border-amber-200 ring-4 ring-amber-50' : 'border-slate-200 opacity-80'}`}>
                 <div className="absolute top-0 right-0 p-8 opacity-5 pointer-events-none -rotate-12"><ShieldAlert size={120} className="text-amber-500"/></div>
                 
                 <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-4">
                       <div className={`w-14 h-14 rounded-[1.5rem] flex items-center justify-center transition-colors ${badWordsEnabled ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400'}`}>
                          <Filter size={24} />
                       </div>
                       <div>
                          <h4 className="text-lg font-black text-slate-900 italic">Description Sanitization</h4>
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Description Blacklist</p>
                       </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer scale-110">
                       <input type="checkbox" className="sr-only peer" checked={badWordsEnabled} onChange={(e) => setBadWordsEnabled(e.target.checked)} />
                       <div className="w-12 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-500 shadow-inner"></div>
                    </label>
                 </div>

                 {badWordsEnabled && (
                    <div className="space-y-6 animate-in slide-in-from-top-4 duration-300">
                       <div className="flex gap-3">
                          <input
                            type="text"
                            className="flex-1 px-6 py-3.5 bg-slate-50 border border-slate-200 rounded-2xl text-xs font-black outline-none focus:bg-white focus:ring-4 focus:ring-amber-50 focus:border-amber-400 transition-all border-b-4 shadow-inner italic"
                            placeholder="Add blacklisted JD keyword..."
                            value={newBadWord}
                            onChange={(e) => setNewBadWord(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addWord(newBadWord, 'bad')}
                          />
                          <button onClick={() => addWord(newBadWord, 'bad')} className="px-6 bg-amber-500 text-white rounded-2xl hover:bg-amber-600 shadow-xl shadow-amber-100 transition-all active:scale-95"><Plus size={20}/></button>
                       </div>
                       <div className="flex flex-wrap gap-2">
                          {badWords.map(w => (
                             <span key={w} className="px-4 py-2 bg-amber-50 text-amber-600 border border-amber-100 rounded-xl text-xs font-black italic flex items-center gap-2 group/tag transition-all hover:bg-white">
                                {w} <X size={14} className="cursor-pointer hover:text-amber-900" onClick={() => removeWord(w, 'bad')} />
                             </span>
                          ))}
                       </div>
                    </div>
                 )}
              </div>
           </div>

           <div className="lg:col-span-4 space-y-8">
              <div className="bg-slate-900 rounded-[3rem] p-10 text-white shadow-2xl relative overflow-hidden group border border-slate-800">
                 <div className="absolute top-0 right-0 p-8 opacity-10 -rotate-12 group-hover:rotate-0 transition-transform duration-700"><ShieldCheck size={120} className="text-blue-400"/></div>
                 <h5 className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-8 relative z-10">Filtering Hierarchy</h5>
                 
                 <div className="space-y-8 relative z-10">
                    {[
                      { icon: ShieldAlert, color: 'text-rose-500', title: "1. Skip Words", desc: "Matched entries are skipped instantly before processing." },
                      { icon: Target, color: 'text-blue-500', title: "2. Inclusion Targets", desc: "Entries must match at least one keyword to proceed." },
                      { icon: Filter, color: 'text-amber-500', title: "3. description Cleanse", desc: "Final verification against JD content blacklist." }
                    ].map((step, i) => (
                      <div key={i} className="flex gap-4">
                        <div className={`shrink-0 p-2 bg-white/5 rounded-xl border border-white/10 ${step.color}`}><step.icon size={16}/></div>
                        <div>
                           <div className="text-xs font-black uppercase italic tracking-tight">{step.title}</div>
                           <div className="text-[10px] text-slate-400 mt-1 leading-relaxed">{step.desc}</div>
                        </div>
                      </div>
                    ))}
                 </div>
                 
                 <div className="mt-12 pt-8 border-t border-white/10 relative z-10">
                    <button 
                      onClick={saveFilters}
                      disabled={syncing}
                      className={`w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-2 disabled:opacity-50 italic shadow-xl ${
                        saveSuccess 
                          ? 'bg-emerald-500 text-white shadow-emerald-500/20' 
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-500/20'
                      }`}
                    >
                       {syncing ? <RefreshCw size={16} className="animate-spin" /> : saveSuccess ? <Check size={16} /> : <Zap size={16} className="fill-white" />}
                       {syncing ? 'Synchronizing...' : saveSuccess ? 'Saved' : 'Save Engine Config'}
                    </button>
                    <p className="text-[9px] text-center text-slate-500 mt-4 uppercase font-bold tracking-widest">Global Filter Sync Enabled</p>
                 </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-[2.5rem] p-8">
                 <div className="flex items-center gap-2 mb-4 text-blue-600">
                    <AlertCircle size={18} />
                    <span className="text-xs font-black uppercase tracking-widest">Logic Insight</span>
                 </div>
                 <p className="text-xs font-medium text-slate-600 leading-relaxed italic">
                    Strict filtering improves your <span className="text-blue-600 font-black">AI Accuracy Score</span>. Use broader filters if you're not seeing many matches, and tighten them down to avoid irrelevant roles.
                  </p>
              </div>
           </div>

        </div>
      </div>
    </DashboardLayout>
  );
};

export default FilterSettings;
